import { Briefcase, ChevronDown, Crown, Home, LogOut, Pin, PinOff, Users } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../hooks/useNavigation";
import httpService from "../services/httpService";
import { APP_HEADER_HEIGHT } from "../utils/layoutConstants";
import AlphaModeControl from "./alpha/AlphaModeControl";
import { useNotifications } from "../contexts/NotificationsContext";
import { formatUnreadPill } from "./notifications/useNotificationBell";
import { hasNavigationChildren, isNavigationChildActive, isNavigationItemActive } from "../utils/navigationItems";
// import ThemeToggle from "./ThemeToggle"; // hidden -- dark mode only

const SIDEBAR_PIN_KEY = "sidebar-pinned";
const AUTO_COLLAPSE_MS = 30_000;

const Sidebar: React.FC = () => {
  const [isPinned, setIsPinned] = useState<boolean>(() => {
    return localStorage.getItem(SIDEBAR_PIN_KEY) === "true";
  });
  const [isAutoCollapsed, setIsAutoCollapsed] = useState<boolean>(!isPinned);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isCollapsed = !isPinned && isAutoCollapsed;

  const clearAutoCollapseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAutoCollapseTimer = useCallback(() => {
    clearAutoCollapseTimer();
    if (isPinned) return;
    timerRef.current = setTimeout(() => {
      setIsAutoCollapsed(true);
    }, AUTO_COLLAPSE_MS);
  }, [isPinned, clearAutoCollapseTimer]);

  useEffect(() => {
    if (!isPinned) {
      startAutoCollapseTimer();
    }
    return clearAutoCollapseTimer;
  }, [isPinned, startAutoCollapseTimer, clearAutoCollapseTimer]);

  const handleMouseEnter = () => {
    if (isPinned) return;
    clearAutoCollapseTimer();
    setIsAutoCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (isPinned) return;
    startAutoCollapseTimer();
  };

  const togglePin = () => {
    const next = !isPinned;
    setIsPinned(next);
    localStorage.setItem(SIDEBAR_PIN_KEY, String(next));
    if (next) {
      clearAutoCollapseTimer();
      setIsAutoCollapsed(false);
    } else {
      startAutoCollapseTimer();
    }
  };
  const [showLogoSpin, setShowLogoSpin] = useState<boolean>(false);

  const triggerSpin = useCallback(() => {
    setShowLogoSpin(false);
    requestAnimationFrame(() => {
      setShowLogoSpin(true);
      const stop = setTimeout(() => setShowLogoSpin(false), 2000);
      return () => clearTimeout(stop);
    });
  }, []);

  // Spin on mount
  useEffect(() => {
    const startTimer = setTimeout(triggerSpin, 300);
    return () => clearTimeout(startTimer);
  }, [triggerSpin]);

  // Spin on expand
  useEffect(() => {
    if (!isCollapsed) {
      triggerSpin();
    }
  }, [isCollapsed, triggerSpin]);

  const [providerLogoUrl, setProviderLogoUrl] = useState<string | null>(null);
  const [loadingLogo, setLoadingLogo] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, userConfig } = useAuth();
  const { unreadCount } = useNotifications();
  const unreadPill = formatUnreadPill(unreadCount);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigation = (path: string): void => {
    navigate(path);
  };

  const userRoles = user?.userRoles?.map(ur => ur.role) || [];
  const hasRole = (role: string) => userRoles.includes(role);
  const isAdmin = hasRole("ADMIN");

  // Check if user has IBKR account connected
  const isIbkrConnected = Boolean(userConfig?.ibkrAccountId);

  const { menuItems, bottomMenuItems, isProviderMode, isAdvisorMode, isAdminMode } = useNavigation({
    isUserAdmin: isAdmin,
  });

  // Fetch provider logo when in provider mode
  useEffect(() => {
    const fetchProviderLogo = async () => {
      if (!isProviderMode || !user) {
        setProviderLogoUrl(null);
        return;
      }

      const providerId = user?.provider?.id || user?.userProviders?.[0]?.providerId;
      if (!providerId) {
        return;
      }

      try {
        setLoadingLogo(true);
        const response = await httpService.get(`/providers/${providerId}/logo`);
        if (response?.data?.logoUrl) {
          setProviderLogoUrl(response.data.logoUrl);
        } else {
          setProviderLogoUrl(null);
        }
      } catch (error) {
        setProviderLogoUrl(null);
      } finally {
        setLoadingLogo(false);
      }
    };

    fetchProviderLogo();
  }, [isProviderMode, user]);

  const isProvider = hasRole("PROVIDER");
  const isAdvisor = hasRole("ADVISOR");
  const isInvestor = hasRole("INVESTOR") || userRoles.length === 0; // Default to investor if no roles

  const logoHomePath = isAdminMode
    ? "/admin/dashboard"
    : isProviderMode
    ? "/provider/dashboard"
    : isAdvisorMode
    ? "/advisor/clients"
    : "/alpha";

  const isSingleItemMenu = menuItems.length === 1;

  // Collapsible groups (PWA-2748): a group is open when toggled open or when
  // the route is inside it; entering a group's route re-opens it.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  useEffect(() => {
    menuItems.forEach(item => {
      if (hasNavigationChildren(item) && isNavigationItemActive(item, location.pathname)) {
        setOpenGroups(current => (current[item.id] ? current : { ...current, [item.id]: true }));
      }
    });
  }, [menuItems, location.pathname]);

  return (
    <div
      data-testid="app-sidebar"
      className={`bg-rp-black border-r border-rp-separator hidden md:flex flex-col transition-all duration-300 ${isCollapsed ? "w-16" : "w-48"}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Header */}
      <div
        className={`flex items-center ${
          isCollapsed ? "flex-col justify-center px-2 gap-4" : "flex-row justify-between px-6"
        } ${APP_HEADER_HEIGHT} border-b border-rp-separator`}
      >
        {!isCollapsed && (
          <div className="flex items-center cursor-pointer" onClick={() => handleNavigation(logoHomePath)}>
            <img src="/favicon white.png" alt="Plutus" className={`h-8 w-8 mr-2 ${showLogoSpin ? "animate-logo-spin" : ""}`} />
            <img src="/dark mode text.png" alt="Plutus" className="h-8 w-auto" />
            {isProviderMode && (
              <span className="ml-2 text-xs font-medium text-rp-amber">
                for Providers
              </span>
            )}
            {isAdvisorMode && (
              <span className="ml-2 text-xs font-medium text-rp-amber">
                for Advisors
              </span>
            )}
            {isAdminMode && (
              <span className="ml-2 text-xs font-medium text-red-400">Admin</span>
            )}
          </div>
        )}
        {isCollapsed && <img src="/favicon white.png" alt="Plutus" className={`h-8 w-8 mx-auto cursor-pointer ${showLogoSpin ? "animate-logo-spin" : ""}`} onClick={() => handleNavigation(logoHomePath)} />}
        {!isCollapsed && (
          <button
            onClick={togglePin}
            className="p-1.5 rounded-lg hover:bg-rp-separator text-rp-grey transition-colors"
            title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
          >
            {isPinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Provider Name and Logo */}
      {isProviderMode && !isCollapsed && (
        <div className="px-6 pt-7 pb-6">
          <div className="flex flex-col items-center">
            {/* Provider Logo */}
            <div className="flex-shrink-0">
              {loadingLogo ? (
                <div className="w-24 h-24 rounded-lg flex items-center justify-center bg-rp-separator">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rp-amber"></div>
                </div>
              ) : providerLogoUrl ? (
                <img
                  src={providerLogoUrl}
                  alt="Provider logo"
                  className="w-24 h-24 object-cover rounded-lg"
                  onError={e => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-24 h-24 rounded-lg flex items-center justify-center bg-rp-separator">
                  <Briefcase className="w-12 h-12 text-rp-amber" />
                </div>
              )}
            </div>
            {/* Provider Name */}
            <div className="w-full text-center">
              <p className="text-xs font-medium text-rp-grey mt-2">
                {user?.provider?.name || user?.userProviders?.[0]?.providers?.name || "Provider"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Provider Logo Only (Collapsed) */}
      {isProviderMode && isCollapsed && (
        <div className="px-2 pt-4 pb-4">
          {loadingLogo ? (
            <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center bg-rp-separator">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rp-amber"></div>
            </div>
          ) : providerLogoUrl ? (
            <img
              src={providerLogoUrl}
              alt="Provider logo"
              className="w-12 h-12 mx-auto object-cover rounded-lg"
              onError={e => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
              }}
            />
          ) : (
            <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center bg-rp-separator">
              <Briefcase className="w-6 h-6 text-rp-amber" />
            </div>
          )}
        </div>
      )}

      {/* Main Navigation */}
      <nav
        className={`flex-1 px-3 space-y-1 overflow-y-auto ${
          isSingleItemMenu ? "flex flex-col justify-center mt-0" : "mt-4"
        }`}
      >
        {menuItems.map(item => {
          const Icon = item.icon;
          const isGroup = hasNavigationChildren(item);
          const isActive = isGroup ? isNavigationItemActive(item, location.pathname) : location.pathname === item.path;
          const isOpen = isGroup && !isCollapsed && Boolean(openGroups[item.id]);

          return (
            <React.Fragment key={item.id}>
              <button
                onClick={() =>
                  isGroup && !isCollapsed
                    ? setOpenGroups(current => ({ ...current, [item.id]: !current[item.id] }))
                    : handleNavigation(item.path)
                }
                aria-label={isCollapsed ? item.label : undefined}
                title={isCollapsed ? item.label : undefined}
                aria-expanded={isGroup && !isCollapsed ? isOpen : undefined}
                aria-controls={isGroup && !isCollapsed ? `sidebar-group-${item.id}` : undefined}
                className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-all ${
                  isCollapsed ? "justify-center px-2 py-2.5" : "px-4 py-2.5"
                } ${
                  isActive
                    ? "bg-rp-amber/10 text-rp-amber border-l-[3px] border-rp-amber"
                    : "text-rp-grey hover:text-rp-grey hover:bg-rp-separator border-l-[3px] border-transparent"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge && (
                      <span className="rounded bg-rp-amber/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-rp-amber">
                        {item.badge}
                      </span>
                    )}
                    {isGroup && (
                      <ChevronDown
                        aria-hidden="true"
                        className={`h-3.5 w-3.5 shrink-0 opacity-80 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
                      />
                    )}
                  </>
                )}
              </button>
              {isOpen && (
                <div id={`sidebar-group-${item.id}`} role="group" aria-label={`${item.label} pages`} className="ml-[15px] space-y-0.5 border-l border-rp-separator pl-[11px]">
                  {(item.children ?? []).map(child => {
                    const childActive = isNavigationChildActive(child, location.pathname);
                    return (
                      <button
                        key={child.id}
                        onClick={() => handleNavigation(child.path)}
                        aria-current={childActive ? "page" : undefined}
                        className={`w-full truncate rounded-md px-2.5 py-[7px] text-left text-xs transition-colors ${
                          childActive
                            ? "bg-rp-amber/10 font-semibold text-rp-amber"
                            : "text-gray-400 hover:bg-rp-separator hover:text-rp-grey"
                        }`}
                      >
                        {child.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* IBKR Connection Status */}
      {!isCollapsed && (
        <div
          onClick={() => handleNavigation("/alpha/profile")}
          className="mx-4 mb-10 p-3 rounded-lg cursor-pointer transition-all bg-gray-900 border border-gray-800"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-white">IBKR Status</span>
            <div className="flex items-center space-x-1">
              <div
                className={`w-2 h-2 rounded-full ${isIbkrConnected ? "bg-green-400" : "bg-yellow-400"} ${
                  isIbkrConnected ? "animate-pulse" : ""
                }`}
              ></div>
              <span className="text-xs text-rp-grey">
                {isIbkrConnected ? "Live" : "Pending"}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <img src="/ibkr.png" alt="IBKR" className="w-4 h-4 object-contain" />
            <span
              className={`text-xs font-medium ${
                isIbkrConnected ? "text-green-400" : "text-rp-amber"
              }`}
            >
              {isIbkrConnected ? "Connected" : "Click to Connect"}
            </span>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="border-t border-rp-separator p-2 space-y-1">
        {/* Role-based Navigation Links */}
        <AlphaModeControl collapsed={isCollapsed} />

        {/* Admin Dashboard Link - Show if user has ADMIN role and not currently in admin mode */}
        {isAdmin && !isAdminMode && (
          <button
            onClick={() => handleNavigation("/admin/dashboard")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-red-700 text-white hover:bg-red-900/20 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Crown className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Admin Dashboard</span>}
          </button>
        )}

        {/* Provider Dashboard Link - Show if user has PROVIDER role and not currently in provider mode */}
        {isProvider && !isProviderMode && (
          <button
            onClick={() => handleNavigation("/provider/dashboard")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-rp-amber text-white hover:bg-rp-amber/20 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Briefcase className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Provider Dashboard</span>}
          </button>
        )}

        {/* Advisor Dashboard Link - Show if user has ADVISOR or ADMIN role and not currently in advisor mode */}
        {(isAdvisor || isAdmin) && !isAdvisorMode && (
          <button
            onClick={() => handleNavigation("/advisor/clients")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-rp-amber text-white hover:bg-rp-amber/20 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Users className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Advisor Dashboard</span>}
          </button>
        )}

        {/* Main Dashboard Link - Show if user is currently in provider/advisor/admin mode AND has INVESTOR role */}
        {(isProviderMode || isAdvisorMode || isAdminMode) && isInvestor && (
          <button
            onClick={() => handleNavigation("/alpha")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-blue-700 bg-blue-900/20 text-blue-300 hover:bg-blue-800/40 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Home className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Main Dashboard</span>}
          </button>
        )}
        {/* Theme toggle hidden -- dark mode only */}
        {bottomMenuItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors ${
                isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
              } ${
                isActive
                  ? "bg-rp-amber/10 text-rp-amber border-l-[3px] border-rp-amber"
                  : "text-rp-grey hover:text-rp-grey hover:bg-rp-separator border-l-[3px] border-transparent"
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!isCollapsed && <span>{item.label}</span>}
              {item.id === "notifications" && unreadPill && (
                <span
                  data-testid="sidebar-unread-pill"
                  className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rp-amber px-1 text-[10px] font-semibold leading-none text-white ${isCollapsed ? "absolute -right-1 -top-1" : "ml-auto"}`}
                >
                  {unreadPill}
                </span>
              )}
            </button>
          );
        })}

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors text-red-500 hover:bg-red-500/20 hover:text-red-500 ${
            isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
          }`}
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
